import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Archive, ArchiveRestore, KeyRound, Loader2, Pencil, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PanelTokensSection } from '@/pages/PanelTokens';
import { RuchApiSection } from '@/pages/RuchApi';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import {
  createAccount,
  fetchAccountScopes,
  fetchModuleCatalog,
  listAccounts,
  setAccountPassword,
  updateAccount,
  type AccountRow,
} from '@/lib/accounts-api';

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// ---------------------------------------------------------------- formularz konta

/** Wartość oznaczająca „bez zawężenia" w liście wyboru — `null` nie przejdzie przez `<option>`. */
const SDI_SCOPE = 'sdi';

const AccountFormSchema = z.object({
  email: z.string().trim().min(3, 'Podaj e-mail').max(320),
  name: z.string().trim().max(200).optional(),
  role: z.enum(['ADMIN', 'USER']),
  password: z.string().max(512).optional(),
  /**
   * ZAKRES konta — wybór WYMUSZONY, bez wartości domyślnej.
   *
   * Puste znaczy „nie wybrano" i nie przejdzie walidacji. Gdyby lista startowała na „SDI",
   * kliknięcie „zapisz" bez zastanowienia dawałoby konto widzące wszystkich klientów — czyli
   * dziurę wyglądającą na przeoczenie, a nie na decyzję. Serwer trzyma tę samą zasadę.
   */
  scope: z.string().min(1, 'Wybierz zakres konta'),
});

type AccountFormValues = z.infer<typeof AccountFormSchema>;

function AccountFormModal({
  open,
  onOpenChange,
  editing,
  moduleCatalog,
  clientAllowed,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: AccountRow | null;
  moduleCatalog: string[];
  /** Uprawnienia, jakie wolno nadać koncie KLIENTA — sufit z §8a, liczony po stronie serwera. */
  clientAllowed: string[];
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  const [modules, setModules] = useState<string[]>(editing?.modules ?? []);

  const scopes = useQuery({ queryKey: ['accounts', 'scopes'], queryFn: fetchAccountScopes, enabled: !editing });
  // konto zawężone ma dokładnie jedną możliwość — wtedy nie ma czego wymuszać, wybieramy ją
  const onlyScope =
    scopes.data && !scopes.data.allowSdi && scopes.data.clients.length === 1 ? scopes.data.clients[0]!.id : '';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AccountFormValues>({
    resolver: zodResolver(AccountFormSchema),
    defaultValues: {
      email: editing?.email ?? '',
      name: editing?.name ?? '',
      role: editing?.role ?? 'USER',
      password: '',
      // przy EDYCJI pole nie jest wyświetlane ani wysyłane, ale musi przejść walidację —
      // zakresu nie da się zmienić edycją, bo to zmiana uprawnień, nie zmiana pola
      scope: editing ? (editing.clientId ?? SDI_SCOPE) : onlyScope,
    },
  });

  const role = watch('role');
  const scope = watch('scope');

  /**
   * Czy to konto KLIENTA — decyduje o sufycie uprawnień (§8a).
   *
   * Przy edycji z wiersza, bo zakresu nie da się zmienić. Przy zakładaniu z wybranego zakresu,
   * ale dopiero gdy jakiś WYBRANO: puste pole nie może udawać konta klienta, bo wyszarzyłoby
   * wszystkie checkboxy jeszcze przed decyzją.
   */
  const isClientAccount = editing ? editing.clientId !== null : scope !== SDI_SCOPE && scope !== '';

  /**
   * Wysyłamy to, co widać na ekranie. Checkbox poza sufitem rysuje się jako odznaczony,
   * więc gdyby stan trzymał go dalej, zapis leciałby z uprawnieniem, którego operator nie
   * widzi — i wracał z odmową serwera bez czytelnego powodu.
   */
  const modulesToSend = isClientAccount ? modules.filter((m) => clientAllowed.includes(m)) : modules;

  const onSubmit = async (values: AccountFormValues) => {
    setServerError(null);
    try {
      if (editing) {
        await updateAccount(editing.id, {
          name: values.name?.trim() ? values.name.trim() : null,
          role: values.role,
          modules: modulesToSend,
        });
        toast.success(t('accounts.saved'));
      } else {
        if (values.password && values.password.length < 8) {
          setServerError(t('accounts.passwordTooShort'));
          return;
        }
        await createAccount({
          email: values.email,
          name: values.name?.trim() || undefined,
          role: values.role,
          modules: modulesToSend,
          password: values.password?.trim() || undefined,
          clientId: values.scope === SDI_SCOPE ? null : values.scope,
        });
        toast.success(t('accounts.created'));
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  const toggleModule = (key: string) =>
    setModules((prev) => (prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={editing ? t('accounts.editTitle') : t('accounts.addTitle')}
        description={editing ? editing.email : t('accounts.addHint')}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            {!editing && (
              <Field label={t('accounts.email')} htmlFor="email" required error={errors.email?.message}>
                <Input id="email" type="email" autoComplete="off" {...register('email')} />
              </Field>
            )}

            <Field label={t('accounts.name')} htmlFor="name" error={errors.name?.message}>
              <Input id="name" autoComplete="off" {...register('name')} />
            </Field>

            <Field label={t('accounts.role')} htmlFor="role" required>
              <Select id="role" {...register('role')}>
                <option value="USER">{t('role.USER')}</option>
                <option value="ADMIN">{t('role.ADMIN')}</option>
              </Select>
            </Field>

            {/* ZAKRES tylko przy zakładaniu. Przy edycji go nie ma i to nie przeoczenie:
                przeniesienie konta między klientami to zmiana uprawnień, nie edycja pola. */}
            {!editing && (
              <Field
                label={t('accounts.scope')}
                htmlFor="scope"
                required
                error={errors.scope?.message}
                hint={t('accounts.scopeHint')}
              >
                <Select id="scope" {...register('scope')}>
                  <option value="">{t('accounts.scopePick')}</option>
                  {scopes.data?.allowSdi && <option value={SDI_SCOPE}>{t('accounts.scopeSdi')}</option>}
                  {(scopes.data?.clients ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {/* Konto SDI widzi WSZYSTKICH klientów — to ma być widoczne w chwili wyboru,
                a nie odkryte później przy audycie. */}
            {!editing && watch('scope') === SDI_SCOPE && (
              <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
                {t('accounts.scopeSdiWarn')}
              </p>
            )}

            {!editing && (
              <Field
                label={t('accounts.password')}
                htmlFor="password"
                error={errors.password?.message}
                hint={t('accounts.passwordHint')}
              >
                <Input id="password" type="password" autoComplete="new-password" {...register('password')} />
              </Field>
            )}

            <Field
              label={t('accounts.modules')}
              hint={
                // trzy różne prawdy, więc trzy różne podpowiedzi — „admin ma wszystko" jest
                // NIEPRAWDĄ dla admina klienta i taka podpowiedź wprowadzałaby w błąd
                isClientAccount
                  ? t('accounts.modulesClientHint')
                  : role === 'ADMIN'
                    ? t('accounts.modulesAdminHint')
                    : t('accounts.modulesHint')
              }
            >
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {moduleCatalog.map((key) => {
                  /**
                   * Uprawnienie poza sufitem konta klienta — checkbox zablokowany, nie ukryty.
                   * Ukrycie wyglądałoby jak brak takiego modułu w portalu; zablokowany
                   * z podpowiedzią mówi, że moduł istnieje, ale nie dla tego rodzaju konta.
                   */
                  const beyondCeiling = isClientAccount && !clientAllowed.includes(key);
                  return (
                    <label
                      key={key}
                      title={beyondCeiling ? t('accounts.moduleNotForClient') : undefined}
                      className={cn(
                        'flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm has-checked:border-primary has-checked:bg-primary/5',
                        beyondCeiling ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--primary)]"
                        checked={modules.includes(key) && !beyondCeiling}
                        onChange={() => toggleModule(key)}
                        disabled={role === 'ADMIN' || beyondCeiling}
                      />
                      <span className="truncate">{t(`nav.${key}`)}</span>
                    </label>
                  );
                })}
              </div>
            </Field>

            <ModalError>{serverError}</ModalError>
          </FormGrid>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- zmiana hasła

function PasswordModal({
  target,
  onClose,
}: {
  target: AccountRow | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    if (password.length < 8) {
      setError(t('accounts.passwordTooShort'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setAccountPassword(target.id, password);
      toast.success(t('accounts.passwordChanged'));
      setPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={target !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('accounts.passwordTitle')} description={target?.email}>
        <form onSubmit={submit} noValidate>
          <FormGrid>
            <Field label={t('accounts.newPassword')} htmlFor="newpw" required hint={t('accounts.passwordHint')}>
              <Input
                id="newpw"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <ModalError>{error}</ModalError>
          </FormGrid>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- strona

export function Accounts() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 25,
    sort: 'email',
    dir: 'asc',
  });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccountRow | null>(null);
  const [pwTarget, setPwTarget] = useState<AccountRow | null>(null);

  const params = { ...state, includeArchived };

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['users', params],
    queryFn: () => listAccounts(params),
  });

  const { data: catalog } = useQuery({ queryKey: ['users', 'modules'], queryFn: fetchModuleCatalog });

  const archiveMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => updateAccount(id, { active }),
    onSuccess: (_res, vars) => {
      toast.success(vars.active ? t('accounts.restored') : t('accounts.archived'));
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error')),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  const columns = useMemo<ColumnDef<AccountRow>[]>(
    () => [
      {
        id: 'email',
        header: t('accounts.email'),
        meta: { exportValue: (r) => r.email },
        cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
      },
      {
        id: 'name',
        header: t('accounts.name'),
        meta: { exportValue: (r) => r.name ?? '' },
        cell: ({ row }) => row.original.name ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'role',
        header: t('accounts.role'),
        meta: { exportValue: (r) => r.role },
        cell: ({ row }) => (
          <Badge variant={row.original.role === 'ADMIN' ? 'default' : 'neutral'}>{t(`role.${row.original.role}`)}</Badge>
        ),
      },
      {
        // Zakres w KOLUMNIE, nie w szczegółach: „które konto widzi którego klienta" to pytanie,
        // które zadaje się o całą listę naraz, a nie o jedno konto.
        id: 'scope',
        header: t('accounts.scope'),
        enableSorting: false,
        meta: { exportValue: (r) => r.clientName ?? 'SDI' },
        cell: ({ row }) =>
          row.original.clientName ? (
            <span className="text-xs">{row.original.clientName}</span>
          ) : (
            <Badge variant="warning" title={t('accounts.scopeSdiWarn')}>
              {t('accounts.scopeSdiShort')}
            </Badge>
          ),
      },
      {
        id: 'modules',
        header: t('accounts.modules'),
        enableSorting: false,
        meta: { exportValue: (r) => (r.role === 'ADMIN' ? 'wszystkie' : r.modules.join(', ')) },
        cell: ({ row }) =>
          row.original.role === 'ADMIN' ? (
            <span className="text-xs text-muted-foreground">{t('accounts.allModules')}</span>
          ) : row.original.modules.length === 0 ? (
            <span className="text-xs text-muted-foreground">—</span>
          ) : (
            <span className="text-xs">{row.original.modules.length}</span>
          ),
      },
      {
        id: 'active',
        header: t('accounts.status'),
        enableSorting: false,
        meta: { exportValue: (r) => (r.active ? 'aktywne' : 'archiwum'), align: 'center' },
        cell: ({ row }) =>
          row.original.active ? (
            <Badge variant="success">{t('accounts.active')}</Badge>
          ) : (
            <Badge variant="warning">{t('accounts.archived_')}</Badge>
          ),
      },
      {
        id: 'lastLoginAt',
        header: t('accounts.lastLogin'),
        meta: { exportValue: (r) => fmtDate(r.lastLoginAt) },
        cell: ({ row }) => <span className="text-xs text-muted-foreground">{fmtDate(row.original.lastLoginAt)}</span>,
      },
      {
        id: 'actions',
        header: t('table.actions'),
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const r = row.original;
          const self = r.id === me?.id;
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                title={t('common.edit')}
                aria-label={`${t('common.edit')} ${r.email}`}
                onClick={() => {
                  setEditing(r);
                  setFormOpen(true);
                }}
              >
                <Pencil />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title={t('accounts.passwordTitle')}
                aria-label={`${t('accounts.passwordTitle')} ${r.email}`}
                onClick={() => setPwTarget(r)}
              >
                <KeyRound />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={self || archiveMut.isPending}
                title={self ? t('accounts.cannotSelf') : r.active ? t('accounts.archive') : t('accounts.restore')}
                aria-label={r.active ? t('accounts.archive') : t('accounts.restore')}
                onClick={() => archiveMut.mutate({ id: r.id, active: !r.active })}
              >
                {r.active ? <Archive /> : <ArchiveRestore />}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, me?.id, archiveMut],
  );

  /** Eksport obejmuje CAŁY wynik filtrowania, nie tylko widoczną stronę. */
  const fetchAllRows = useCallback(async (): Promise<AccountRow[]> => {
    const out: AccountRow[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await listAccounts({ ...params, page, pageSize: 200 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params]);

  return (
    <>
      <DataTable<AccountRow>
        title={t('nav.accounts')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName="konta-portalu"
        fetchAllRows={fetchAllRows}
        reportKey="accounts"
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus />
            {t('accounts.add')}
          </Button>
        }
        /**
         * ⚠️ TU FILTR ROZSZERZA LISTĘ, a nie przycina — i pasek ma o tym mówić tym samym
         * kształtem, bo pytanie człowieka jest to samo: „dlaczego widzę to, co widzę".
         */
        aktywneFiltry={includeArchived ? [t('accounts.showArchived')] : []}
        onWyczyscFiltry={() => {
          setIncludeArchived(false);
          onStateChange({ page: 1 });
        }}
        filters={
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-3.5 accent-[var(--primary)]"
              checked={includeArchived}
              onChange={(e) => {
                setIncludeArchived(e.target.checked);
                onStateChange({ page: 1 });
              }}
            />
            {t('accounts.showArchived')}
          </label>
        }
      />

      {formOpen && (
        <AccountFormModal
          key={editing?.id ?? 'new'}
          open={formOpen}
          onOpenChange={setFormOpen}
          editing={editing}
          moduleCatalog={catalog?.modules ?? []}
          clientAllowed={catalog?.clientAllowed ?? []}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['users'] })}
        />
      )}

      <PasswordModal target={pwTarget} onClose={() => setPwTarget(null)} />

      {/*
        Dostęp MASZYNOWY na tym samym ekranie, ale wizualnie osobno. Ekran odpowiada na
        pytanie „kto ma dostęp do tej instancji", a token panelu jest dostępem — tylko nie
        dla człowieka. Sekcja sama się nie rysuje dla konta zawężonego do klienta.
      */}
      <PanelTokensSection />
      {/* Ruch integracji tuż pod tokenami: kto ma token i co przez niego przechodzi, w jednym miejscu. */}
      <RuchApiSection />
    </>
  );
}
